/**
 * FuarAlaniProfilModal.jsx — edit a fairground's künye, address, map pin and logo (backlog #51 follow-up, 2026-10-20).
 *
 * One modal for the three things the "Künye" and "Konum" cards show. Mounted only while open (the parent renders it
 * conditionally), so its fields start from the venue as it is NOW and nothing has to be reset.
 *
 *   - Logo: its own request, sent the moment a file is chosen (POST /logo), like the company pages; the venue is reloaded after it.
 *   - Künye, address, pin: ONE request on Kaydet (PUT /profile, all four keys; a blank text clears it, both coordinates empty
 *     removes the pin). The pin is set by typing the numbers or by clicking the map (FirmaKonumHarita in its editable mode).
 */
import React, { useRef, useState } from "react";
import { Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import FirmaKonumHarita from "../Firma/FirmaKonumHarita";
import { firmaFileSrc, koordinatHatasi } from "../../services/firmaMediaService";
import {
  gorselUzantisiGecerli, hataMetni, updateFuarAlaniProfile, uploadFuarAlaniLogo,
} from "../../services/fuarAlaniEkBilgiService";

const bos = (v) => v === null || v === undefined || String(v).trim() === "";

export default function FuarAlaniProfilModal({ fuarAlaniId, alani, onClose, onChanged }) {
  const { t: tx } = useTranslation("yonetim");
  const dosyaRef = useRef(null);
  const [kunye, setKunye] = useState(alani?.fuarAlaniKunye || "");
  const [adres, setAdres] = useState(alani?.fuarAlaniAddress || "");
  const [lat, setLat] = useState(alani?.latitude != null ? String(alani.latitude) : "");
  const [lng, setLng] = useState(alani?.longitude != null ? String(alani.longitude) : "");
  const [logoUrl, setLogoUrl] = useState(alani?.fuarAlaniLogoUrl || null);
  const [hata, setHata] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [logoYukleniyor, setLogoYukleniyor] = useState(false);

  const latNum = !bos(lat) && Number.isFinite(Number(lat)) ? Number(lat) : null;
  const lngNum = !bos(lng) && Number.isFinite(Number(lng)) ? Number(lng) : null;

  async function logoSecildi(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!gorselUzantisiGecerli(file)) { setHata(tx("fuarAlaniDetay.gorselTuruGecersiz")); return; }
    setLogoYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await uploadFuarAlaniLogo(fuarAlaniId, file);
      setLogoUrl(sonuc?.data?.fuarAlaniLogoUrl || logoUrl);
      await onChanged?.();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.logoYuklenemedi")));
    } finally {
      setLogoYukleniyor(false);
    }
  }

  async function kaydet() {
    const kh = koordinatHatasi(lat, lng);
    if (kh) { setHata(kh); return; }
    setKaydediliyor(true);
    setHata(null);
    try {
      await updateFuarAlaniProfile(fuarAlaniId, {
        kunye, address: adres,
        latitude: bos(lat) ? null : Number(lat),
        longitude: bos(lng) ? null : Number(lng),
      });
      await onChanged?.();
      onClose();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.profilKaydedilemedi")));
      setKaydediliyor(false);
    }
  }

  return (
    <Modal isOpen toggle={onClose} size="lg" centered scrollable>
      <ModalHeader toggle={onClose}>{tx("fuarAlaniDetay.profilDuzenle")}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}

        <Label className="form-label">{tx("fuarAlaniDetay.logo")}</Label>
        <div className="d-flex align-items-center gap-3 mb-3">
          {logoUrl
            ? <img src={firmaFileSrc(logoUrl)} alt={tx("fuarAlaniDetay.fuarAlaniLogosu")} style={{ maxWidth: 160, maxHeight: 80, objectFit: "contain" }} />
            : <span className="text-muted small">{tx("fuarAlaniDetay.logoYok")}</span>}
          <input ref={dosyaRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="d-none" onChange={logoSecildi} />
          <Button size="sm" color="primary" outline disabled={logoYukleniyor} onClick={() => dosyaRef.current?.click()}>
            {logoYukleniyor
              ? <Spinner size="sm" />
              : <><Upload size={14} className="me-1" />{logoUrl ? tx("fuarAlaniDetay.logoDegistir") : tx("fuarAlaniDetay.logoYukle")}</>}
          </Button>
        </div>

        <Label className="form-label" for="fa-kunye">{tx("fuarAlaniDetay.kunye")}</Label>
        <Input
          id="fa-kunye" type="textarea" rows={4} className="mb-3" value={kunye}
          placeholder={tx("fuarAlaniDetay.kunyePlaceholder")} onChange={(e) => setKunye(e.target.value)}
        />

        <Label className="form-label" for="fa-adres">{tx("fuarAlaniDetay.adres")}</Label>
        <Input
          id="fa-adres" type="textarea" rows={2} className="mb-3" value={adres}
          placeholder={tx("fuarAlaniDetay.adresPlaceholder")} onChange={(e) => setAdres(e.target.value)}
        />

        <Label className="form-label">{tx("fuarAlaniDetay.konum")}</Label>
        <Row className="g-2 mb-2 align-items-end">
          <Col sm={4}>
            <Label className="form-label small text-muted mb-1" for="fa-enlem">{tx("fuarAlaniDetay.enlem")}</Label>
            <Input id="fa-enlem" bsSize="sm" value={lat} inputMode="decimal" onChange={(e) => setLat(e.target.value)} />
          </Col>
          <Col sm={4}>
            <Label className="form-label small text-muted mb-1" for="fa-boylam">{tx("fuarAlaniDetay.boylam")}</Label>
            <Input id="fa-boylam" bsSize="sm" value={lng} inputMode="decimal" onChange={(e) => setLng(e.target.value)} />
          </Col>
          <Col sm="auto">
            <Button size="sm" color="light" disabled={bos(lat) && bos(lng)} onClick={() => { setLat(""); setLng(""); }}>
              {tx("fuarAlaniDetay.pinKaldir")}
            </Button>
          </Col>
        </Row>
        <div className="rounded-3 overflow-hidden border">
          <FirmaKonumHarita
            lat={latNum} lng={lngNum} editable height={280}
            onPick={(la, lo) => { setLat(String(la)); setLng(String(lo)); }}
          />
        </div>
        <div className="text-muted small mt-1">{tx("fuarAlaniDetay.haritadaIsaretle")}</div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={kaydediliyor}>{tx("common:iptal")}</Button>
        <Button color="primary" onClick={kaydet} disabled={kaydediliyor || logoYukleniyor}>
          {kaydediliyor ? <Spinner size="sm" /> : tx("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
